import React, {
  useCallback,
  useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BreadcrumbContainer from '../../common/breadcrumbContainer/breadcrumbContainer';

import ExportSelector from './ExportsSelector';
import CustomExportTable from './CustomExportsTable';

import useViewableCustomExports from './useViewableCustomExports';
import { getUnions } from '../../unions/state/unions.actions';
import { getCustomers } from '../../contacts/customers/state/customers.actions';
import { getEquipment } from '../../equipment/state/equipment.actions';
import { getBuckets } from '../../buckets/state/buckets.actions';
import ABExportBatchTable from './ABExportBatchTable';

const CRUMBS = [{
  text: 'Exports',
  icon: 'table',
}];

function Exports() {
  const dispatch = useDispatch();

  const { paidFlags = [] } = useSelector((state) => state.settings.company) || {};
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const [exportList] = useViewableCustomExports();

  const [selectedExport, setSelectedExport] = useState();
  const [headerHeight, setHeightHeight] = useState(32);

  const onHeaderResize = useCallback((e) => {
    const { height: newHeight } = e ?? {};
    if (!newHeight) return;
    setHeightHeight(newHeight);
  }, []);

  const filteredExports = useMemo(() => {
    const exports = exportList.filter((ex) => selectedDivisions.has(ex.divisionId));
    if (paidFlags.includes('AB_WORK_REPORTING')) {
      exports.push({
        id: 'abWorkReporting',
        title: 'AB Work Reporting',
        type: 'abWorkReporting',
        isABWorkReporting: true,
      });
    }
    return exports;
  }, [exportList, selectedDivisions, paidFlags]);

  const onExportSelected = useCallback((exportId) => {
    const newSelected = filteredExports.find((customExport) => customExport.id === exportId);
    setSelectedExport(newSelected);
  }, [filteredExports]);

  const fullCrumbs = useMemo(() => {
    if (!filteredExports || filteredExports.length === 0 || !filteredExports) return CRUMBS;
    return CRUMBS.concat([{
      text: (
        <ExportSelector
          exportList={exportList}
          selectedExport={selectedExport}
          onExportSelected={onExportSelected}
        />
      ),
    }]);
  }, [filteredExports, selectedExport, onExportSelected]);

  useEffect(() => {
    dispatch(getUnions());
    dispatch(getCustomers());
    dispatch(getEquipment());
    dispatch(getBuckets());
  }, [dispatch]);

  useEffect(() => {
    if (filteredExports.length > 0
        && !selectedDivisions.has(selectedExport?.divisionId) && !selectedExport?.isABWorkReporting
    ) {
      setSelectedExport(filteredExports[0]);
    } else if (filteredExports.length === 0 && selectedExport) {
      setSelectedExport();
    }
  }, [filteredExports, selectedExport, selectedDivisions]);

  const scroll = useMemo(() => (
    window.innerHeight - 325 - (headerHeight - 32)
  ), [headerHeight]);

  return (
    <BreadcrumbContainer crumbs={fullCrumbs}>
      {selectedExport?.isABWorkReporting
        ? (
          <ABExportBatchTable />
        ) : (
          <CustomExportTable
            selectedExport={selectedExport}
            scroll={scroll}
            onHeaderResize={onHeaderResize}
            canFilter
          />
        )
      }
    </BreadcrumbContainer>
  );
}

export default Exports;
